/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon } from '@fluentui/react';
import { Stack } from '@fluentui/react/lib/Stack';
import { Observer, observer } from 'mobx-react';
import React from 'react';
import { defineMessages } from 'react-intl';
import { useHistory } from 'react-router';
import { CluIntentDto } from 'src/clu/appShell/cluBuildSchemaPage/components/CluIntentsList';
import { LuisText } from 'src/core/components/LuisText';
import { LuisTooltip } from 'src/core/components/LuisTooltip';
import { NameCellLink } from 'src/core/components/NameCellLink';
import { Table } from 'src/core/components/Table';
import { TableColumnProps, TableProps } from 'src/core/components/TableProps';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getScopedTheme, getStylist } from 'src/core/themes/luisStylist';
import { isDevelopmentEnvironment } from 'src/core/utils/api/environment';
import { globalMessages } from 'src/core/utils/globalMessages';
import { routeTable } from 'src/core/utils/routeTable';
import styled from 'styled-components';

export type CluIntentsTableProps = TableProps<CluIntentDto>;

const messages = defineMessages({
    intents: { id: 'clu.cluIntentsTable.intents', defaultMessage: 'Intents' },
    taggedUtterances: { id: 'clu.cluIntentsTable.taggedUtterances', defaultMessage: 'Labeled utterances' },
    servicesAttached: { id: 'clu.cluIntentsTable.servicesAttached', defaultMessage: 'Connected project' },
    entitiesUsed: { id: 'clu.cluIntentsTable.entitiesUsed', defaultMessage: 'Entities used with this intent' }
});

const theme = getScopedTheme('CluIntentsTable');

const { styleDiv } = getStylist('CluIntentsTable');
const EntitiesListWrapper = styleDiv('EntitiesListWrapper', { display: 'flex', whiteSpace: 'break-spaces', flexWrap: 'wrap' });

const ErrorIcon = styled(Icon)({
    color: theme.errorIconColor
});

export const CluIntentsTable = observer((props: CluIntentsTableProps) => {
    const history = useHistory();
    const { localize } = useLocalization();
    const { rootStore, dispatcher } = useGlobalState();
    const { items, sortKey, selection } = props;

    const { name: currentProjectName, projectType, intentStoreHouse } = rootStore.currentCluProjectStore;
    const isOrchestrationProject = projectType === 'Orchestration';
    const routes = routeTable[isOrchestrationProject ? 'orchestration' : 'clu'];
    const linkedIntents = intentStoreHouse.items.filter(intent => Boolean(intent.orchestrationInfo));

    React.useEffect(() => {
        if (isOrchestrationProject) {
            if (linkedIntents.some(intent => intent.orchestrationType === 'Luis')) {
                dispatcher.dispatch('loadLuisApps');
            }

            if (linkedIntents.some(intent => intent.orchestrationType === 'Conversation')) {
                dispatcher.dispatch('loadCluProjects');
            }

            if (linkedIntents.some(intent => intent.orchestrationType === 'QuestionAnswering')) {
                dispatcher.dispatch('getQnaMakerProjects');
            }
        }
    }, [isOrchestrationProject]);

    const onModelClick = (modelName: string, modelType: 'intent' | 'entity') => {
        const state: { intentsIdsFilters: string[]; entitiesIdsFilters: string[] } = { intentsIdsFilters: [], entitiesIdsFilters: [] };

        modelType === 'intent' ? state.intentsIdsFilters.push(modelName) : state.entitiesIdsFilters.push(modelName);

        history.push(routes.dataTaggingPage(currentProjectName), state);
    };

    const onRenderEntitiesUsedColumn = (item: CluIntentDto) => (
        <EntitiesListWrapper>
            {item.entities.map((entity, index) => (
                <NameCellLink key={`${entity}-${index}`} onClick={() => history.push(routes.entityDetailsPage(currentProjectName, entity))}>
                    {index === item.entities.length - 1 ? entity : `${entity},`}
                    &nbsp;
                </NameCellLink>
            ))}
        </EntitiesListWrapper>
    );

    const onRenderOrchestrationColumn = (item: CluIntentDto) => {
        switch (item.orchestrationInfo?.orchestrationType) {
            case 'Conversation':
                const { projectName } = item.orchestrationInfo;

                return (
                    <NameCellLink target="_blank" href={routeTable.clu.projectPage(projectName)}>
                        {projectName} (Conversational Language Understanding)
                    </NameCellLink>
                );
            case 'Luis':
                const { appId, versionId } = item.orchestrationInfo;

                // This line is required to ensure observability.
                const areAppsLoading = rootStore.luisAppStoreHouse.tracker.state !== 'completed';
                const appName = rootStore.luisAppStoreHouse.findById(appId)?.name;
                const url = (isDevelopmentEnvironment() ? `${window.location.origin}` : 'https://www.luis.ai/').concat(
                    routeTable.luis.intentsPage(appId, versionId)
                );

                if (!rootStore.hasPermissionToListLUISApps) {
                    return (
                        <LuisTooltip content={localize(globalMessages.cannotListLuisApps)}>
                            <ErrorIcon iconName="ErrorBadge" />
                        </LuisTooltip>
                    );
                }

                if (areAppsLoading || !appName) {
                    return null;
                }

                return (
                    <NameCellLink target="_blank" href={url}>
                        {appName} (LUIS)
                    </NameCellLink>
                );
            case 'QuestionAnswering':
                // This line is required to ensure observability.
                const areQnaProjectsLoading = rootStore.qnaProjectStoreHouse.tracker.state !== 'completed';
                const qnaProjectName = item.orchestrationInfo.projectName;

                if (areQnaProjectsLoading || !qnaProjectName) {
                    return null;
                }

                return (
                    <NameCellLink target="_blank" href={routeTable.qnaMaker.projectPage(qnaProjectName)}>
                        {qnaProjectName} (Question Answering)
                    </NameCellLink>
                );
            default:
                return <LuisText>-</LuisText>;
        }
    };

    const columns: TableColumnProps[] = [
        {
            minWidth: 250,
            maxWidth: 300,
            key: 'intentName',
            isRowHeader: true,
            isResizable: true,
            isSortable: true,
            isCollapsible: false,
            fieldName: 'intentName',
            name: localize(messages.intents),
            isSorted: sortKey?.key === 'intentName',
            isSortedDescending: sortKey?.key === 'intentName' && sortKey?.order === 'desc',
            onRender: (item: CluIntentDto) =>
                item.orchestrationInfo ? (
                    <LuisText variant="mediumPlus">{item.intentName}</LuisText>
                ) : (
                    <NameCellLink onClick={() => onModelClick(item.intentName, 'intent')}>{item.intentName}</NameCellLink>
                )
        },
        {
            minWidth: 140,
            maxWidth: 140,
            isResizable: true,
            isSortable: true,
            key: 'labelsCount',
            isRowHeader: false,
            isCollapsible: false,
            fieldName: 'labelsCount',
            isSorted: sortKey?.key === 'labelsCount',
            name: localize(messages.taggedUtterances),
            isSortedDescending: sortKey?.key === 'labelsCount' && sortKey?.order === 'desc',
            onRender: (item: CluIntentDto) => (
                <Stack horizontalAlign="center" grow>
                    <LuisText>{item.orchestrationInfo ? '-' : item.labelsCount}</LuisText>
                </Stack>
            )
        },
        isOrchestrationProject
            ? {
                  minWidth: 200,
                  maxWidth: 400,
                  key: 'services',
                  isResizable: true,
                  isSortable: false,
                  isRowHeader: false,
                  isCollapsible: true,
                  name: localize(messages.servicesAttached),
                  onRender: (item: CluIntentDto) => <Observer>{() => onRenderOrchestrationColumn(item)}</Observer>
              }
            : {
                  minWidth: 350,
                  maxWidth: 550,
                  key: 'entities',
                  isResizable: true,
                  isSortable: false,
                  isRowHeader: false,
                  isCollapsible: true,
                  onRender: onRenderEntitiesUsedColumn,
                  name: localize(messages.entitiesUsed)
              }
    ];

    return <Table<CluIntentDto> {...props} columns={columns} items={items} selection={selection} />;
});
